import { hqiMiddlewareApiInstance } from './apiInstance';
import { ErrorLog } from './ErrorLog';
import {
  NOTIFICATIONS_PAGE_SIZE,
  NotificationsResult,
} from '../components/Notification/NotificationTypes';

/**
 * These endpoints return HTTP 200 even on failure, signalling the real outcome
 * with `error: true` and an inner `status` (e.g. 403). So the resolved value
 * has to be inspected before its payload is trusted -- doubly so here, because
 * the axios interceptors also resolve rather than reject on 400/409 and hand
 * back undefined on a network/CORS failure.
 */
const isEnvelopeOk = (response: any) =>
  response?.status === 200 && response?.data && !response.data.error;

/**
 * GET {middleware}/notifications/list/{projectId}
 *
 * Returns null on any failure -- unreachable endpoint, error envelope or a
 * malformed body. Callers render an empty list for null; there is no mock
 * fallback, so the panel never shows data the server didn't send.
 */
export const fetchNotificationsList = async (
  projectId: string | number,
  offset = 0,
  unreadOnly = false,
): Promise<NotificationsResult | null> => {
  try {
    const response = await hqiMiddlewareApiInstance.get(
      `notifications/list/${projectId}`,
      { params: { unread_only: unreadOnly, limit: NOTIFICATIONS_PAGE_SIZE, offset } },
    );

    const result = response?.data?.response;
    if (!isEnvelopeOk(response) || !result || !Array.isArray(result.notifications)) {
      throw new Error(response?.data?.message || 'Invalid notifications response');
    }

    return result;
  } catch (error) {
    ErrorLog('fetchNotificationsList', error);
    return null;
  }
};

/**
 * POST {middleware}/notifications/mark-read/{projectId}
 *
 * Omitting notificationId marks every unread notification in the workspace.
 * Returns the updated count, or null when the call failed -- callers use that
 * to roll back an optimistic update.
 */
export const markNotificationsRead = async (
  projectId: string | number,
  notificationId?: number,
): Promise<number | null> => {
  try {
    const body = notificationId === undefined ? {} : { notification_id: notificationId };
    const response = await hqiMiddlewareApiInstance.post(
      `notifications/mark-read/${projectId}`,
      body,
    );

    if (!isEnvelopeOk(response)) {
      throw new Error(response?.data?.message || 'Unable to mark notification read');
    }
    return response?.data?.response?.updated_count ?? 0;
  } catch (error) {
    ErrorLog('markNotificationsRead', error);
    return null;
  }
};
